import React from 'react';
import { useTranslation } from 'react-i18next';
import { ExperimentProgress as IExperimentProgress, formatDuration, getStatusColor } from '../../types/experiment';

interface ExperimentProgressProps {
    progress: IExperimentProgress;
    showDetails?: boolean;
}

const ExperimentProgress: React.FC<ExperimentProgressProps> = ({ progress, showDetails = false }) => {
    const { t } = useTranslation();

    const progressPercent = progress.total_items > 0
        ? (progress.processed_items / progress.total_items) * 100
        : 0;

    const isRunning = progress.run_status === 'running';
    const isPaused = progress.run_status === 'paused';
    const isCompleted = progress.run_status === 'completed';
    const isFailed = progress.run_status === 'failed';

    const getElapsedTime = () => {
        if (!progress.started_at) return null;
        const start = new Date(progress.started_at).getTime();
        const end = progress.finished_at
            ? new Date(progress.finished_at).getTime()
            : Date.now();
        return formatDuration(end - start);
    };

    const getEstimatedRemaining = () => {
        if (!progress.started_at || progress.processed_items === 0) return null;
        const start = new Date(progress.started_at).getTime();
        const elapsed = Date.now() - start;
        const remaining = progress.total_items - progress.processed_items;
        const avgTimePerItem = elapsed / progress.processed_items;
        return formatDuration(remaining * avgTimePerItem);
    };

    const elapsedTime = getElapsedTime();
    const estimatedRemaining = isRunning ? getEstimatedRemaining() : null;

    return (
        <div className="experiment-progress">
            <div className="progress-header">
                <span
                    className="progress-status"
                    style={{ color: getStatusColor(progress.run_status) }}
                >
                    <span className="material-icons">
                        {isRunning ? 'sync' : isPaused ? 'pause' : isCompleted ? 'check_circle' : isFailed ? 'error' : 'pending'}
                    </span>
                    {t(`experiments.status.${progress.run_status}`) || progress.run_status}
                </span>
                <span className="progress-count">
                    {progress.processed_items} / {progress.total_items}
                </span>
            </div>

            <div className="progress-bar">
                <div
                    className={`progress-fill ${progress.run_status}`}
                    style={{ width: `${progressPercent}%` }}
                />
            </div>

            <div className="progress-info">
                <span className="progress-percent">{progressPercent.toFixed(1)}%</span>
                {progress.current_batch > 0 && progress.total_batches > 0 && (
                    <span className="progress-batch">
                        {t('experiments.progress.batch')} {progress.current_batch}/{progress.total_batches}
                    </span>
                )}
            </div>

            {showDetails && (
                <div className="progress-details">
                    {elapsedTime && (
                        <div className="detail-item">
                            <span className="material-icons">timer</span>
                            <span className="detail-label">{t('experiments.progress.elapsed')}</span>
                            <span className="detail-value">{elapsedTime}</span>
                        </div>
                    )}
                    {estimatedRemaining && (
                        <div className="detail-item">
                            <span className="material-icons">hourglass_empty</span>
                            <span className="detail-label">{t('experiments.progress.remaining')}</span>
                            <span className="detail-value">{estimatedRemaining}</span>
                        </div>
                    )}
                    {progress.started_at && (
                        <div className="detail-item">
                            <span className="material-icons">play_arrow</span>
                            <span className="detail-label">{t('experiments.progress.startedAt')}</span>
                            <span className="detail-value">
                                {new Date(progress.started_at).toLocaleString()}
                            </span>
                        </div>
                    )}
                    {progress.finished_at && (
                        <div className="detail-item">
                            <span className="material-icons">stop</span>
                            <span className="detail-label">{t('experiments.progress.finishedAt')}</span>
                            <span className="detail-value">
                                {new Date(progress.finished_at).toLocaleString()}
                            </span>
                        </div>
                    )}
                </div>
            )}

            {/* Error summary */}
            {progress.errors && progress.errors.length > 0 && (
                <div className="progress-errors">
                    <div className="error-header">
                        <span className="material-icons">warning</span>
                        {t('experiments.progress.errors', { count: progress.errors.length })}
                    </div>
                    {showDetails && (
                        <ul className="error-list">
                            {progress.errors.slice(0, 5).map((error, index) => (
                                <li key={index} className="error-item">
                                    <span className="error-item-id">{error.item_id}</span>
                                    <span className="error-item-message">{error.error_message}</span>
                                </li>
                            ))}
                            {progress.errors.length > 5 && (
                                <li className="error-more">
                                    +{progress.errors.length - 5} {t('experiments.progress.moreErrors')}
                                </li>
                            )}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

export default ExperimentProgress;
